/**
 * Free Gift Promotion - Buy X Get Y with Discount Code Input
 * Shows discount code input when cart total exceeds $200
 * When customer enters "HALLOWEENBREW", adds variety-12-pack as free gift and applies discount
 */

class FreeGiftPromotion {
  constructor() {
    this.THRESHOLD_AMOUNT = 20000; // $200.00 in cents
    this.FREE_GIFT_HANDLE = 'variety-12-pack';
    this.DISCOUNT_CODE = 'HALLOWEENBREW';
    this.freeGiftAdded = false;
    this.isProcessing = false;
    this.discountInputAdded = false;
    
    this.init();
  }

  init() {
    // Subscribe to cart updates
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        this.handleCartUpdate(event);
      });
    }

    // Check cart on page load
    this.checkCartOnLoad();

    // Set up mutation observer to watch for cart drawer changes
    this.setupCartDrawerObserver();
  }

  setupCartDrawerObserver() {
    // Watch for changes to the cart drawer DOM
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && window.MutationObserver) {
      this.cartDrawerObserver = new MutationObserver((mutations) => {
        // Check if discount input was removed
        const discountContainer = document.getElementById('free-gift-discount-container');
        if (!discountContainer && !this.isProcessing) {
          console.log('Free Gift Promotion: Detected discount input removal, re-adding');
          this.discountInputAdded = false;
          setTimeout(() => {
            this.getCartData().then(cartData => {
              this.checkThresholdAndShowDiscountInput(cartData);
            });
          }, 100);
        }
      });

      this.cartDrawerObserver.observe(cartDrawer, {
        childList: true,
        subtree: true
      });
    }
  }

  async checkCartOnLoad() {
    try {
      const cartData = await this.getCartData();
      this.checkThresholdAndShowDiscountInput(cartData);
    } catch (error) {
      console.error('Free Gift Promotion: Error checking cart on load:', error);
    }
  }

  async getCartData() {
    const response = await fetch('/cart.js');
    if (!response.ok) {
      throw new Error('Failed to fetch cart data');
    }
    return await response.json();
  }

  async getProductData(handle) {
    const response = await fetch(`/products/${handle}.js`);
    if (!response.ok) {
      throw new Error(`Failed to fetch product data for ${handle}`);
    }
    return await response.json();
  }

  handleCartUpdate(event) {
    if (this.isProcessing) return;
    
    console.log('Free Gift Promotion: Cart update event received', event.source || 'unknown source');
    
    // Get cart data from the event or fetch it
    let cartData = event.cartData || event.data?.cartData;
    
    if (cartData) {
      this.checkThresholdAndShowDiscountInput(cartData);
    } else {
      // Fallback to fetch cart data
      this.getCartData().then(data => {
        this.checkThresholdAndShowDiscountInput(data);
      }).catch(error => {
        console.error('Free Gift Promotion: Error fetching cart data:', error);
      });
    }

    // Additional safeguard: ensure discount input is always present after any cart update
    setTimeout(() => {
      const discountContainer = document.getElementById('free-gift-discount-container');
      if (!discountContainer) {
        console.log('Free Gift Promotion: Discount input missing after cart update, re-adding');
        this.discountInputAdded = false; // Reset flag
        this.getCartData().then(cartData => {
          this.checkThresholdAndShowDiscountInput(cartData);
        });
      }
    }, 200);
  }

  async checkThresholdAndShowDiscountInput(cartData) {
    if (this.isProcessing) return;
    
    const cartTotal = cartData.total_price;
    const hasFreeGift = this.hasVarietyPackInCart(cartData);
    
    console.log('Free Gift Promotion: Checking cart - Total:', (cartTotal / 100).toFixed(2), 'Threshold:', (this.THRESHOLD_AMOUNT / 100).toFixed(2), 'Has Free Gift:', hasFreeGift, 'Input Added:', this.discountInputAdded);
    
    // Always show discount input box (for testing and ease of use)
    if (!this.discountInputAdded) {
      console.log('Free Gift Promotion: Showing discount input (always visible mode)');
      this.showDiscountCodeInput();
    }

    // Always update the discount input visibility when cart updates
    setTimeout(() => {
      this.updateDiscountInputVisibility(cartData);
    }, 100);
  }

  hasVarietyPackInCart(cartData) {
    // STRICTLY check for FREE GIFT variety packs only - must have _free_gift property
    const hasFreeGiftVariety = cartData.items.some(item => {
      const itemHandle = item.handle || item.product_handle;
      const hasHandle = itemHandle === this.FREE_GIFT_HANDLE;
      const isFreeGift = item.properties && item.properties._free_gift === 'true';
      const isVarietyProduct = item.product_title && item.product_title.toLowerCase().includes('variety');
      
      // CRITICAL: Only return true if the item has the _free_gift property set to 'true'
      // Regular purchased variety packs should NOT count as free gifts
      const isActualFreeGift = isFreeGift && (hasHandle || isVarietyProduct);
      
      console.log('Free Gift Promotion: Checking cart item:', {
        handle: itemHandle,
        title: item.product_title,
        hasHandle,
        isFreeGift,
        isVarietyProduct,
        properties: item.properties,
        isActualFreeGift
      });
      
      return isActualFreeGift;
    });
    
    console.log('Free Gift Promotion: Has ACTUAL FREE GIFT variety pack in cart:', hasFreeGiftVariety);
    return hasFreeGiftVariety;
  }

  showDiscountCodeInput() {
    if (this.discountInputAdded) return;

    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) return;

    // Find the checkout button or cart footer - look for more specific selectors
    const checkoutButton = cartDrawer.querySelector('.cart__ctas');
    const cartFooter = cartDrawer.querySelector('.cart-drawer__footer, .cart__ctas, .drawer__footer');
    
    console.log('Free Gift Promotion: Found checkout button:', !!checkoutButton, 'Found cart footer:', !!cartFooter);
    
    if (!checkoutButton && !cartFooter) {
      console.warn('Free Gift Promotion: Could not find checkout button or footer to place discount input');
      // Try to find any button that might be the checkout
      const anyButton = cartDrawer.querySelector('button');
      console.log('Free Gift Promotion: Found any button:', !!anyButton);
      return;
    }

    // Create discount code input container
    const discountContainer = document.createElement('div');
    discountContainer.className = 'free-gift-discount-container';
    discountContainer.id = 'free-gift-discount-container';
    discountContainer.style.cssText = `
      margin-bottom: 10px;
      animation: slideInFromTop 0.3s ease-out;
    `;

    discountContainer.innerHTML = `
  
      <div style="display: flex; gap: 8px; align-items: center;">
        <input 
          type="text" 
          id="free-gift-discount-code" 
          placeholder="Enter discount code"
          style="
            flex: 1;
            padding: 10px 12px;
            border: 1px solid #465A34;
            font-size: 1.2rem;
            background: white;
            color: #465A34;
          "
        />
        <button 
          type="button" 
          id="apply-free-gift-discount"
          style="
            padding: 10px 16px;
            background: #465A34;
            color: #D6E331;
            border: none;
           
            font-weight: bold;
            font-size: 1.2rem;
            cursor: pointer;
          "
        >
          Apply
        </button>
      </div>
      <div id="discount-message" style="margin-top: 8px; font-size: 0.8rem; text-align: center;"></div>
    `;

    // Insert the discount container ABOVE the checkout button
    if (checkoutButton) {
      // Insert before the checkout button in its parent container
      const parent = checkoutButton.parentNode;
      parent.insertBefore(discountContainer, checkoutButton);
      console.log('Free Gift Promotion: Inserted before checkout button');
    } else if (cartFooter) {
      // Insert at the beginning of the cart footer
      cartFooter.insertBefore(discountContainer, cartFooter.firstChild);
      console.log('Free Gift Promotion: Inserted at beginning of cart footer');
    } else {
      // Fallback: try to find the cart contents area and append there
      const cartContents = cartDrawer.querySelector('.drawer__contents, .cart-drawer__form, cart-drawer-items');
      if (cartContents) {
        cartContents.appendChild(discountContainer);
        console.log('Free Gift Promotion: Appended to cart contents as fallback');
      }
    }

    // Add event listeners
    this.setupDiscountInputEvents();
    this.discountInputAdded = true;

    // Update the promotional message with current cart data
    setTimeout(() => {
      this.getCartData().then(cartData => {
        this.updatePromotionalMessage(cartData);
      });
    }, 100);

    console.log('Free Gift Promotion: Discount input added to cart drawer');
  }

  hideDiscountCodeInput() {
    const discountContainer = document.getElementById('free-gift-discount-container');
    if (discountContainer) {
      discountContainer.style.animation = 'slideOutToTop 0.3s ease-in';
      setTimeout(() => {
        discountContainer.remove();
        this.discountInputAdded = false;
      }, 300);
    }
  }

  setupDiscountInputEvents() {
    const input = document.getElementById('free-gift-discount-code');
    const button = document.getElementById('apply-free-gift-discount');
    const messageDiv = document.getElementById('discount-message');

    if (!input || !button) return;

    // Handle apply button click
    button.addEventListener('click', () => {
      this.handleDiscountCodeSubmission(input.value.trim(), messageDiv);
    });

    // Handle enter key in input
    input.addEventListener('keypress', (e) => {
      if (e.key === 'Enter') {
        this.handleDiscountCodeSubmission(input.value.trim(), messageDiv);
      }
    });

    // Handle input styling on focus
    input.addEventListener('focus', () => {
      input.style.borderColor = '#465A34';
      input.style.outline = '2px solid rgba(70, 90, 52, 0.2)';
    });

    input.addEventListener('blur', () => {
      input.style.outline = 'none';
    });
  }

  async handleDiscountCodeSubmission(code, messageDiv) {
    if (this.isProcessing) return;
    
    if (!code) {
      this.showDiscountMessage(messageDiv, 'Please enter a discount code', 'error');
      return;
    }

    if (code.toUpperCase() !== this.DISCOUNT_CODE.toUpperCase()) {
      this.showDiscountMessage(messageDiv, 'Invalid discount code', 'error');
      return;
    }

    // Check if cart still meets threshold
    const cartData = await this.getCartData();
    if (cartData.total_price < this.THRESHOLD_AMOUNT) {
      this.showDiscountMessage(messageDiv, `Cart total must be $${(this.THRESHOLD_AMOUNT / 100).toFixed(2)} or more`, 'error');
      return;
    }

    // Check if free gift is already in cart
    if (this.hasVarietyPackInCart(cartData)) {
      this.showDiscountMessage(messageDiv, 'Free gift already claimed', 'error');
      return;
    }

    this.showDiscountMessage(messageDiv, 'Adding your free gift...', 'loading');
    await this.addFreeGift(messageDiv);
  }

  showDiscountMessage(messageDiv, message, type = 'info') {
    if (!messageDiv) return;

    let color = '#465A34';
    if (type === 'error') color = '#E94346';
    if (type === 'success') color = '#28a745';
    if (type === 'loading') color = '#465A34';

    messageDiv.style.color = color;
    messageDiv.textContent = message;
  }

  async addFreeGift(messageDiv = null) {
    if (this.isProcessing) return;
    this.isProcessing = true;

    console.log('Free Gift Promotion: Adding free gift...');

    try {
      // Get product data to find the variant ID
      const productData = await this.getProductData(this.FREE_GIFT_HANDLE);
      
      if (!productData || !productData.variants || productData.variants.length === 0) {
        console.error('Free Gift Promotion: Product not found or has no variants:', this.FREE_GIFT_HANDLE);
        throw new Error('Free gift product not found or has no variants');
      }

      console.log('Free Gift Promotion: Found product data:', productData.title);

      // Get the first available variant
      const variant = productData.variants.find(v => v.available) || productData.variants[0];
      
      if (!variant) {
        console.error('Free Gift Promotion: No variant found for product');
        throw new Error('No variant found for free gift product');
      }

      console.log('Free Gift Promotion: Using variant:', variant.id, variant.title);
      
      // Add the free gift to cart
      const addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: variant.id,
          quantity: 1,
          properties: {
            '_free_gift': 'true',
            '_promotion': 'HALLOWEENBREW'
          }
        })
      });

      if (!addResponse.ok) {
        const errorText = await addResponse.text();
        console.error('Free Gift Promotion: Failed to add to cart:', errorText);
        throw new Error(`Failed to add free gift to cart: ${errorText}`);
      }

      const addResult = await addResponse.json();
      console.log('Free Gift Promotion: Successfully added to cart:', addResult);

      this.freeGiftAdded = true;
      
      // Apply discount code
      await this.applyDiscountCode();
      
      // Show success message in discount input
      if (messageDiv) {
        this.showDiscountMessage(messageDiv, '✅ Free gift added successfully!', 'success');
      }
      
      // Show notification
      this.showFreeGiftNotification();
      
      // Hide the discount input after successful redemption
      setTimeout(() => {
        this.hideDiscountCodeInput();
      }, 2000);
      
      // Update cart display
      this.updateCartDisplay();

      console.log('Free Gift Promotion: Process completed successfully');

    } catch (error) {
      console.error('Free Gift Promotion: Error adding free gift:', error);
      
      if (messageDiv) {
        this.showDiscountMessage(messageDiv, 'Unable to add free gift. Please try again.', 'error');
      }
      
      // Show error notification to user
      this.showErrorNotification('Unable to add free gift. Please try again.');
    } finally {
      this.isProcessing = false;
    }
  }

  updateDiscountInputVisibility(cartData) {
    const cartTotal = cartData.total_price;
    const hasFreeGift = this.hasVarietyPackInCart(cartData);
    const discountContainer = document.getElementById('free-gift-discount-container');
    
    // Always show input (for testing and ease of use)
    const shouldShow = true; // Always show for now
    
    console.log('Free Gift Promotion: Updating visibility - Should show:', shouldShow, 'Container exists:', !!discountContainer);
    
    if (shouldShow && !discountContainer) {
      console.log('Free Gift Promotion: Showing discount input (always visible mode)');
      this.showDiscountCodeInput();
    }
    
    // Update the promotional message based on cart status
    this.updatePromotionalMessage(cartData);
  }

  updatePromotionalMessage(cartData) {
    const discountContainer = document.getElementById('free-gift-discount-container');
    if (!discountContainer) return;

    const cartTotal = cartData.total_price;
    const hasFreeGift = this.hasVarietyPackInCart(cartData);
    const messageDiv = discountContainer.querySelector('div:first-child');
    
    if (!messageDiv) return;

    let title, subtitle;
    
    if (hasFreeGift) {
      title = '✅ Free Gift Already Added!';
      subtitle = 'You have already claimed your free variety pack';
    } else if (cartTotal >= this.THRESHOLD_AMOUNT) {
      title = '🎉 Eligible for FREE VARIETY 12 PACK!';
      subtitle = 'Enter discount code to claim your free gift';
    } else {
      const needed = (this.THRESHOLD_AMOUNT - cartTotal) / 100;
      title = '💰 Spend More for Free Gift!';
      subtitle = `Add $${needed.toFixed(2)} more to qualify for free variety pack`;
    }

    // messageDiv.innerHTML = `
    //   <div style="color: #465A34; font-weight: bold; font-size: 0.9rem; margin-bottom: 4px;">
    //     ${title}
    //   </div>
    //   <div style="color: #465A34; font-size: 0.8rem; opacity: 0.8;">
    //     ${subtitle}
    //   </div>
    // `;
  }

  async applyDiscountCode() {
    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          attributes: {
            discount_code: this.DISCOUNT_CODE
          }
        })
      });

      if (response.ok) {
        // Try to apply the discount code via the discount endpoint
        const discountResponse = await fetch('/discount/' + this.DISCOUNT_CODE, {
          method: 'GET'
        });
        
        if (!discountResponse.ok) {
          console.warn('Free Gift Promotion: Discount code may need manual application');
        }
      }
    } catch (error) {
      console.error('Free Gift Promotion: Error applying discount code:', error);
    }
  }

  showFreeGiftNotification() {
    // Create and show notification in cart drawer
    const cartDrawer = document.querySelector('cart-drawer');
    const cartDrawerInner = document.querySelector('.drawer__inner');
    
    if (cartDrawerInner) {
      // Remove any existing notification
      const existingNotification = cartDrawerInner.querySelector('.free-gift-notification');
      if (existingNotification) {
        existingNotification.remove();
      }

      // Create notification element
      const notification = document.createElement('div');
      notification.className = 'free-gift-notification';
      notification.style.cssText = `
        background: #D6E331;
        color: #465A34;
        padding: 16px 16px;
        margin-bottom: 16px;
        font-weight: bold;
        text-align: center;
        animation: slideInFromTop 0.3s ease-out;
        box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        border: 2px solid #465A34;
      `;
      notification.innerHTML = '🎉 Free Gift Added! VARIETY 12 PACK<br><small style="font-size:0.85em;opacity:0.8;">Discount code applied</small>';

      // Add CSS animation
      if (!document.querySelector('#free-gift-notification-styles')) {
        const style = document.createElement('style');
        style.id = 'free-gift-notification-styles';
        style.textContent = `
          @keyframes slideInFromTop {
            from {
              opacity: 0;
              transform: translateY(-20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          .free-gift-notification {
            position: relative;
            z-index: 10;
          }
          @keyframes slideOutToTop {
            from {
              opacity: 1;
              transform: translateY(0);
            }
            to {
              opacity: 0;
              transform: translateY(-20px);
            }
          }
        `;
        document.head.appendChild(style);
      }

      // Insert after drawer header
      const drawerHeader = cartDrawerInner.querySelector('.drawer__header');
      const drawerContents = cartDrawerInner.querySelector('cart-drawer-items');
      
      if (drawerHeader && drawerContents) {
        cartDrawerInner.insertBefore(notification, drawerContents);
      } else if (drawerContents) {
        cartDrawerInner.insertBefore(notification, drawerContents);
      } else {
        cartDrawerInner.appendChild(notification);
      }

      // Auto-remove notification after 8 seconds
      setTimeout(() => {
        if (notification && notification.parentNode) {
          notification.style.animation = 'slideOutToTop 0.3s ease-in';
          setTimeout(() => {
            notification.remove();
          }, 300);
        }
      }, 8000);
    }
  }

  async updateCartDisplay() {
    // Trigger cart update to refresh the display
    if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'free-gift-promotion'
      });
    }

    // Alternative: fetch and update cart sections manually
    try {
      const response = await fetch(`${window.routes?.cart_url || '/cart'}?section_id=cart-drawer`);
      if (response.ok) {
        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        
        // Store current discount input state
        const hadDiscountInput = this.discountInputAdded;
        
        // Update cart drawer content
        const cartDrawerItems = document.querySelector('cart-drawer-items');
        const newCartDrawerItems = doc.querySelector('cart-drawer-items');
        
        if (cartDrawerItems && newCartDrawerItems) {
          cartDrawerItems.innerHTML = newCartDrawerItems.innerHTML;
        }

        // Update cart icon bubble
        const cartIconBubble = document.querySelector('#cart-icon-bubble');
        const newCartIconBubble = doc.querySelector('#cart-icon-bubble');
        
        if (cartIconBubble && newCartIconBubble) {
          cartIconBubble.innerHTML = newCartIconBubble.innerHTML;
        }

        // Reset discount input state since DOM was updated
        this.discountInputAdded = false;

        // ALWAYS re-add the discount input after cart updates
        setTimeout(() => {
          console.log('Free Gift Promotion: Re-adding discount input after cart update');
          this.getCartData().then(cartData => {
            this.checkThresholdAndShowDiscountInput(cartData);
          });
        }, 150);
      }
    } catch (error) {
      console.error('Free Gift Promotion: Error updating cart display:', error);
    }
  }

  showErrorNotification(message) {
    const cartDrawer = document.querySelector('cart-drawer');
    const cartDrawerInner = document.querySelector('.drawer__inner');
    
    if (cartDrawerInner) {
      const notification = document.createElement('div');
      notification.className = 'free-gift-error-notification';
      notification.style.cssText = `
        background: #E94346;
        color: white;
        padding: 12px 16px;
        margin-bottom: 16px;
        border-radius: 8px;
        font-weight: bold;
        text-align: center;
        animation: slideInFromTop 0.3s ease-out;
        box-shadow: 0 2px 8px rgba(0,0,0,0.1);
      `;
      notification.innerHTML = `⚠️ ${message}`;

      const drawerHeader = cartDrawerInner.querySelector('.drawer__header');
      const drawerContents = cartDrawerInner.querySelector('cart-drawer-items');
      
      if (drawerHeader && drawerContents) {
        cartDrawerInner.insertBefore(notification, drawerContents);
      } else if (drawerContents) {
        cartDrawerInner.insertBefore(notification, drawerContents);
      } else {
        cartDrawerInner.appendChild(notification);
      }

      // Auto-remove error notification after 5 seconds
      setTimeout(() => {
        if (notification && notification.parentNode) {
          notification.style.animation = 'slideOutToTop 0.3s ease-in';
          setTimeout(() => {
            notification.remove();
          }, 300);
        }
      }, 5000);
    }
  }

  destroy() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
    if (this.cartDrawerObserver) {
      this.cartDrawerObserver.disconnect();
    }
  }
}

// Initialize the free gift promotion when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.freeGiftPromotion = new FreeGiftPromotion();
  });
} else {
  window.freeGiftPromotion = new FreeGiftPromotion();
}

// Clean up on page unload
window.addEventListener('beforeunload', () => {
  if (window.freeGiftPromotion) {
    window.freeGiftPromotion.destroy();
  }
});

// Add manual trigger for testing (accessible via browser console)
window.testFreeGiftPromotion = () => {
  if (window.freeGiftPromotion) {
    console.log('Manually triggering free gift promotion...');
    window.freeGiftPromotion.getCartData().then(cartData => {
      // Force trigger by setting a high cart total for testing
      cartData.total_price = 25000; // $250
      window.freeGiftPromotion.checkThresholdAndAddGift(cartData);
    });
  } else {
    console.error('Free gift promotion not initialized');
  }
};